/**
 * RequestChooser (iter126) — Consolidated "Request a Session" entry point.
 *
 * Replaces the two separate Simple-Mode tiles ("Book a Session" +
 * "Club Request"). The chooser routes the player to one of three category
 * flows, each backed by the same /api/group-bookings endpoint so the coach
 * only ever sees one queue:
 *
 *   · Solo   — 1:1 coaching (max_players = 1, no invitees)
 *   · Group  — 2-6 friends, split-pay optional
 *   · Club   — 2-8 players, multi-coach cascade + broadcast
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { User, Users, Users2, ArrowRight, Sparkles, Star } from "lucide-react";
import api from "@/lib/api";
import { useUserMode } from "@/context/UserModeContext";

const OPTIONS = [
  {
    key: "solo",
    to: "/group-bookings/new?type=solo",
    title: "Solo Session",
    subtitle: "Just me and a coach",
    hint: "1:1 focused coaching. Pick a coach, propose a time.",
    Icon: User,
    accent: "#66C2FF",
    price: "From £30/hr",
  },
  {
    key: "group",
    to: "/group-bookings/new?type=group",
    title: "Group Session",
    subtitle: "Rally up to 6 friends",
    hint: "Invite mates by name or email. Split the bill or pay for everyone.",
    Icon: Users,
    accent: "#CCFF00",
    price: "From £8-15/player",
  },
  {
    key: "club",
    to: "/group-bookings/new?type=club",
    title: "Club",
    subtitle: "Bigger sessions via the club",
    hint: "Post a club-wide request — coaches respond, players sign up.",
    Icon: Users2,
    accent: "#FF4500",
    price: "Custom quote",
  },
];

export default function RequestChooser() {
  // Iter127 — Personalisation. Backend returns the category the player
  // has used most often; if we have one, decorate that card with a "Your
  // usual" chip so returning players get a one-tap default. Silent-fail:
  // fresh users just see the three options with no chip.
  // Iter128 — Also expose `total` so a first-time visitor (total=0) gets
  // a gentle "Popular first pick" nudge on the Group card instead of an
  // unadorned chooser.
  // Iter137 — Simple Mode surfaces Club as a full-width card below the
  // Solo + Group pair (previous iter136 hid it entirely). Advanced Mode
  // keeps the three-across layout.
  const { isSimple } = useUserMode();
  const [usual, setUsual] = useState(null);
  const [total, setTotal] = useState(null);
  useEffect(() => {
    api.get("/group-bookings/request-preference")
      .then((r) => {
        setUsual(r.data?.most_used_type || null);
        setTotal(typeof r.data?.total === "number" ? r.data.total : null);
      })
      .catch(() => {});
  }, []);

  // In Simple Mode we split the cards into two rows so Club sits full-width
  // beneath Solo + Group. Advanced Mode keeps the flat 3-column grid.
  const topOptions = isSimple ? OPTIONS.filter((o) => o.key !== "club") : OPTIONS;
  const bottomOption = isSimple ? OPTIONS.find((o) => o.key === "club") : null;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 space-y-8" data-testid="request-chooser">
      <header className="fade-up">
        <div className="label-mono text-[#CCFF00] flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> REQUEST A SESSION
        </div>
        <h1 className="h-display text-4xl md:text-5xl mt-2 tracking-tighter">
          What kind of session? <span className="text-[#CCFF00]">Pick one.</span>
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-3 max-w-2xl leading-relaxed">
          Every request lands with a coach for approval. You can propose an exact time or a date range — coaches pick the exact slot when they accept.
        </p>
        {total === 0 && (
          <div
            data-testid="request-first-time-banner"
            className="mt-4 inline-flex items-center gap-2 px-3 py-2 border border-[#CCFF00] bg-[#CCFF0010] text-sm text-white"
          >
            <Sparkles className="w-4 h-4 text-[#CCFF00]" />
            Not sure? <strong>Group Sessions</strong> are where most players start — invite friends and split the bill.
          </div>
        )}
      </header>

      {(() => {
        const renderCard = ({ key, to, title, subtitle, hint, Icon, accent, price }) => {
          const isUsual = usual === key;
          const isFirstTimeHint = !isUsual && total === 0 && key === "group";
          const logPick = () => {
            // Iter133 — Fire-and-forget click analytics. Failure never
            // blocks the navigation.
            try {
              api.post("/chooser-analytics/pick", { category: key }).catch(() => {});
            } catch { /* silent */ }
          };
          return (
            <Link
              key={key}
              to={to}
              onClick={logPick}
              role="menuitem"
              data-testid={`request-option-${key}`}
              className="card-surface p-6 flex flex-col gap-4 group transition-transform hover:-translate-y-0.5 relative"
              style={{ borderColor: isUsual || isFirstTimeHint ? accent : "#262626" }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = accent)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = (isUsual || isFirstTimeHint) ? accent : "#262626")}
            >
              {isUsual && (
                <span
                  data-testid={`request-usual-${key}`}
                  className="absolute -top-2.5 right-4 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold"
                  style={{ background: accent, color: "#0A0A0A" }}
                >
                  <Star className="w-2.5 h-2.5" strokeWidth={3} fill="currentColor" /> Your usual
                </span>
              )}
              {isFirstTimeHint && (
                <span
                  data-testid={`request-first-time-hint-${key}`}
                  className="absolute -top-2.5 right-4 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold"
                  style={{ background: accent, color: "#0A0A0A" }}
                >
                  <Sparkles className="w-2.5 h-2.5" strokeWidth={3} /> Popular first pick
                </span>
              )}
              <span
                style={{
                  width: 56, height: 56, borderRadius: 16,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  background: `${accent}15`, color: accent,
                }}
                aria-hidden
              >
                <Icon strokeWidth={1.5} className="w-7 h-7" />
              </span>

              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">{subtitle}</div>
                <div className="h-display text-2xl mt-1">{title}</div>
                <p className="text-sm text-[#A3A3A3] mt-2 leading-relaxed">{hint}</p>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-[#262626]">
                <span className="label-mono text-xs" style={{ color: accent }}>{price}</span>
                <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-white group-hover:text-[#CCFF00]">
                  {isUsual ? "Book it" : "Start"} <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          );
        };
        return (
          <div className="space-y-4">
            <div
              className={isSimple ? "grid grid-cols-1 md:grid-cols-2 gap-4" : "grid grid-cols-1 md:grid-cols-3 gap-4"}
              role="menu"
              aria-label="Session type options"
            >
              {topOptions.map(renderCard)}
            </div>
            {bottomOption && (
              <div
                className="grid grid-cols-1 gap-4"
                role="menu"
                aria-label="Club session option"
                data-testid="request-chooser-club-row"
              >
                {renderCard(bottomOption)}
              </div>
            )}
          </div>
        );
      })()}

      <div className="border border-[#262626] p-4 text-xs text-[#A3A3A3] leading-relaxed" data-testid="request-chooser-fine-print">
        <span className="text-white font-semibold uppercase tracking-wider">Cancellation:</span> cancelling a confirmed session after payment collects a 10% admin fee from card refunds. Credits are fully restored.
      </div>
    </div>
  );
}
